/**
 * The Media tab of the library: media and folders of the folder being browsed, a breadcrumb back
 * up, and sort controls. Media is filed by dragging it onto a folder (or a breadcrumb); files
 * dropped from the desktop land in the folder they are dropped on.
 */
import {
  createMediaFolder,
  deleteMediaFolder,
  formatDuration,
  mediaFolderPath,
  mediaUsageCount,
  mediaUsageCounts,
  moveMediaFolder,
  moveMediaToFolder,
  removeMedia,
  renameMediaFolder,
  type Id,
  type MediaAsset,
  type MediaFolder,
  type Project,
} from '@ve/core';
import {
  AlertTriangle,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  AudioLines,
  ChevronRight,
  CircleCheck,
  Film,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  Loader2,
  Pencil,
  RotateCw,
  Trash2,
} from 'lucide-react';
import { useEffect, useState, type DragEvent } from 'react';
import { openContextMenu, type MenuEntry } from '../../components/ContextMenu.tsx';
import { insertMedia } from '../../lib/commands.ts';
import { activeDrag, clearActiveDrag, DND_MEDIA, DND_MEDIA_FOLDER } from '../../lib/dnd.ts';
import { tip } from '../../lib/tooltip.ts';
import { useMediaRuntime } from '../../media/service.ts';
import { mediaService } from '../../media/setup.ts';
import { edit, useEditor } from '../../state/editor-store.ts';
import { importMedia, sourcesFromDrop } from '../../state/session.ts';
import { openSourcePreview, useSourcePreview } from '../../state/source-preview.ts';
import { LIBRARY_SORT_KEYS, useUi, type LibrarySortKey } from '../../state/ui-store.ts';
import { captureTime, SORT_DESCRIPTIONS, SORT_LABELS, sortFolders, sortMedia } from './sort.ts';

/** The folder being browsed, or `undefined` for the top level (also when it was deleted). */
export function useCurrentFolder(): Id | undefined {
  const folderId = useUi((s) => s.libraryFolderId);
  const exists = useEditor((s) => (folderId !== undefined ? !!s.project?.mediaFolders[folderId] : false));
  const setFolder = useUi((s) => s.setLibraryFolder);
  useEffect(() => {
    if (folderId !== undefined && !exists) setFolder(undefined);
  }, [folderId, exists, setFolder]);
  return exists ? folderId : undefined;
}

/** Asks for a name and creates a folder inside the one being browsed. */
export function newFolder(parentId: Id | undefined): void {
  const name = prompt('New folder', 'New folder');
  if (!name?.trim()) return;
  edit('New folder', (p) => createMediaFolder(p, name, parentId));
}

/** The folder a media item is listed in (items in a since-deleted folder show at the top level). */
function folderOf(project: Project, asset: MediaAsset): Id | undefined {
  return asset.folderId !== undefined && project.mediaFolders[asset.folderId] ? asset.folderId : undefined;
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

/** The detail line under a card's name, matching what the library is sorted by. */
function detailFor(asset: MediaAsset, key: LibrarySortKey): string | undefined {
  switch (key) {
    case 'captured':
      return dateFormat.format(captureTime(asset));
    case 'imported':
      return dateFormat.format(Date.parse(asset.importedAt));
    case 'size':
      return asset.location.size !== undefined ? formatBytes(asset.location.size) : undefined;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------------------------
// Dropping onto folders
// ---------------------------------------------------------------------------------------------

/** Whether the current library drag may be dropped into `target`. */
function canDropInto(project: Project, target: Id | undefined, dt: DataTransfer): boolean {
  if (activeDrag.mediaId) return !!project.media[activeDrag.mediaId];
  if (activeDrag.folderId) {
    const moving = activeDrag.folderId;
    // Not into itself or its own sub-folders, and not where it already is.
    return (
      !mediaFolderPath(project, target).some((f) => f.id === moving) &&
      project.mediaFolders[moving]?.parentId !== target
    );
  }
  return dt.types.includes('Files');
}

/** Drop-target props for a folder card or breadcrumb that files whatever is dropped into `target`. */
function useFolderDrop(target: Id | undefined) {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver(e: DragEvent) {
        const project = useEditor.getState().project;
        if (!project || !canDropInto(project, target, e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation(); // the panel would otherwise show its own import highlight
        e.dataTransfer.dropEffect = e.dataTransfer.types.includes('Files') ? 'copy' : 'move';
        setOver(true);
      },
      onDragLeave(e: DragEvent) {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      },
      onDrop(e: DragEvent) {
        setOver(false);
        const project = useEditor.getState().project;
        if (!project || !canDropInto(project, target, e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        const name = target === undefined ? 'the top level' : `“${project.mediaFolders[target]?.name}”`;
        if (activeDrag.mediaId) {
          const id = activeDrag.mediaId;
          edit(`Move to ${name}`, (p) => moveMediaToFolder(p, [id], target));
        } else if (activeDrag.folderId) {
          const id = activeDrag.folderId;
          edit(`Move folder to ${name}`, (p) => moveMediaFolder(p, id, target));
        } else {
          const sources = sourcesFromDrop(e.dataTransfer); // must run synchronously in the event
          if (sources)
            void sources.then((list) =>
              importMedia(
                list.map((source) => ({ source })),
                undefined,
                target,
              ),
            );
        }
        clearActiveDrag();
      },
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------------------------

export function MediaTab({ onImport }: { onImport(): void }) {
  const project = useEditor((s) => s.project)!;
  const uploads = useMediaRuntime((s) => s.imports);
  const folderId = useCurrentFolder();
  const sort = useUi((s) => s.librarySort);

  const allItems = project.mediaOrder.map((id) => project.media[id]).filter((m): m is MediaAsset => !!m);
  const folders = sortFolders(Object.values(project.mediaFolders).filter((f) => f.parentId === folderId));
  const items = sortMedia(
    allItems.filter((a) => folderOf(project, a) === folderId),
    project.mediaOrder,
    sort,
  );
  const uploadList = Object.values(uploads);
  const usage = mediaUsageCounts(project);

  if (allItems.length === 0 && folders.length === 0 && folderId === undefined && uploadList.length === 0) {
    return (
      <div className="library-empty" onClick={onImport} {...tip('Browse for files to import')}>
        <FolderOpen size={28} />
        <div>Drop videos, images or audio here</div>
        <div className="muted">or click to browse</div>
      </div>
    );
  }

  return (
    <>
      <LibraryToolbar folderId={folderId} />
      <div className="media-grid">
        {folders.map((folder) => (
          <FolderCard key={folder.id} folder={folder} />
        ))}
        {uploadList.map((u) => (
          <div
            key={u.id}
            className="media-card uploading"
            {...tip(
              u.phase === 'copying' ? `Copying ${u.name} into the media folder` : `Analysing ${u.name}`,
            )}
          >
            <div className="media-thumb">
              <Loader2 className="spin" size={20} />
            </div>
            <div className="media-name">{u.name}</div>
            <div className="progress">
              <div style={{ width: `${Math.round((u.phase === 'copying' ? u.progress : 1) * 100)}%` }} />
            </div>
          </div>
        ))}
        {items.map((asset) => (
          <MediaCard
            key={asset.id}
            asset={asset}
            detail={detailFor(asset, sort.key)}
            uses={usage.get(asset.id) ?? 0}
          />
        ))}
      </div>
      {folders.length === 0 && items.length === 0 && uploadList.length === 0 && (
        <div className="library-folder-empty muted">
          This folder is empty. Drag media onto it from the library, or drop files here.
        </div>
      )}
    </>
  );
}

function LibraryToolbar({ folderId }: { folderId: Id | undefined }) {
  const project = useEditor((s) => s.project)!;
  const sort = useUi((s) => s.librarySort);
  const setSort = useUi((s) => s.setLibrarySort);
  const path = mediaFolderPath(project, folderId).reverse();
  const DirectionIcon = sort.descending ? ArrowUpNarrowWide : ArrowDownNarrowWide;

  return (
    <div className="library-toolbar">
      <nav className="library-crumbs" aria-label="Library folders">
        <Crumb target={undefined} label="All media" current={folderId === undefined} />
        {path.map((folder) => (
          <span key={folder.id} className="library-crumb-step">
            <ChevronRight size={12} className="muted" />
            <Crumb target={folder.id} label={folder.name} current={folder.id === folderId} />
          </span>
        ))}
      </nav>
      <select
        className="select library-sort"
        value={sort.key}
        onChange={(e) => setSort({ ...sort, key: e.target.value as LibrarySortKey })}
        {...tip(`Sort by: ${SORT_DESCRIPTIONS[sort.key]}`)}
      >
        {LIBRARY_SORT_KEYS.map((key) => (
          <option key={key} value={key}>
            {SORT_LABELS[key]}
          </option>
        ))}
      </select>
      <button
        className="btn small icon ghost"
        onClick={() => setSort({ ...sort, descending: !sort.descending })}
        {...tip(sort.descending ? 'Descending — click for ascending' : 'Ascending — click for descending')}
      >
        <DirectionIcon size={14} />
      </button>
    </div>
  );
}

function Crumb({ target, label, current }: { target: Id | undefined; label: string; current: boolean }) {
  const setFolder = useUi((s) => s.setLibraryFolder);
  const drop = useFolderDrop(target);
  return (
    <button
      className={`library-crumb${current ? ' current' : ''}${drop.over ? ' drop-over' : ''}`}
      onClick={() => setFolder(target)}
      {...drop.props}
      {...tip(current ? label : `Go to “${label}” (or drop media here to move it there)`)}
    >
      {label}
    </button>
  );
}

function FolderCard({ folder }: { folder: MediaFolder }) {
  const project = useEditor((s) => s.project)!;
  const setFolder = useUi((s) => s.setLibraryFolder);
  const drop = useFolderDrop(folder.id);
  const mediaCount = Object.values(project.media).filter((m) => m.folderId === folder.id).length;
  const folderCount = Object.values(project.mediaFolders).filter((f) => f.parentId === folder.id).length;
  const summary =
    [
      folderCount > 0 && `${folderCount} folder${folderCount === 1 ? '' : 's'}`,
      mediaCount > 0 && `${mediaCount} item${mediaCount === 1 ? '' : 's'}`,
    ]
      .filter(Boolean)
      .join(', ') || 'Empty';

  const rename = () => {
    const name = prompt('Rename folder', folder.name);
    if (name?.trim()) edit('Rename folder', (p) => renameMediaFolder(p, folder.id, name));
  };
  const remove = () => edit(`Delete folder “${folder.name}”`, (p) => deleteMediaFolder(p, folder.id));

  return (
    <div
      className={`media-card folder-card${drop.over ? ' drop-over' : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_MEDIA_FOLDER, folder.id);
        e.dataTransfer.effectAllowed = 'move';
        activeDrag.folderId = folder.id;
      }}
      onDragEnd={clearActiveDrag}
      onDoubleClick={() => setFolder(folder.id)}
      onContextMenu={(e) => {
        e.preventDefault();
        openContextMenu(e.clientX, e.clientY, [
          { label: 'Open', onSelect: () => setFolder(folder.id) },
          { label: 'Rename…', onSelect: rename },
          'separator',
          {
            label: 'Delete folder',
            description: 'Removes only the folder; its contents move up one level',
            danger: true,
            onSelect: remove,
          },
        ]);
      }}
      {...drop.props}
      {...tip(`${folder.name} — ${summary}\nDouble-click to open; drag media onto it to file it here`)}
    >
      <div className="media-thumb folder-thumb">
        <Folder size={30} />
      </div>
      <div className="media-name">{folder.name}</div>
      <div className="media-detail muted">{summary}</div>
      <div className="media-actions">
        <button
          className="btn ghost small icon"
          {...tip(`Rename “${folder.name}”`)}
          onClick={(e) => {
            e.stopPropagation();
            rename();
          }}
        >
          <Pencil size={12} />
        </button>
        <button
          className="btn ghost small icon"
          {...tip(`Delete the folder “${folder.name}” (its contents move up one level)`)}
          onClick={(e) => {
            e.stopPropagation();
            remove();
          }}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Media card
// ---------------------------------------------------------------------------------------------

const KIND_ICON = { video: Film, audio: AudioLines, image: ImageIcon };

function confirmRemove(asset: MediaAsset): void {
  const project = useEditor.getState().project!;
  const uses = mediaUsageCount(project, asset.id);
  if (uses > 0 && !confirm(`“${asset.name}” is used by ${uses} clip(s). Remove it and those clips?`)) return;
  edit('Remove media', (p) => removeMedia(p, asset.id, true));
}

function MediaCard({
  asset,
  detail,
  uses,
}: {
  asset: MediaAsset;
  detail: string | undefined;
  /** Clips using the asset. */
  uses: number;
}) {
  const media = useMediaRuntime((s) => s.runtime[asset.id]);
  const Icon = KIND_ICON[asset.kind];
  const online = media?.availability === 'online';
  const offline = media?.availability === 'offline' || media?.availability === 'error';
  const derivatives = Object.values(media?.derivatives ?? {});
  const pending = online && derivatives.some((s) => s === 'pending' || s === 'processing');
  const failed = online && derivatives.some((s) => s === 'failed');
  const progress = media?.progress;
  const previewing = useSourcePreview((s) => s.mediaId === asset.id);
  const taken = asset.capturedAt ? `\nTaken ${dateFormat.format(Date.parse(asset.capturedAt))}` : '';

  const contextMenu = (x: number, y: number) => {
    const project = useEditor.getState().project!;
    const parent = mediaFolderPath(project, asset.folderId)[1];
    const items: MenuEntry[] = [{ label: 'Add at playhead', onSelect: () => insertMedia(asset.id) }];
    if (asset.folderId !== undefined && project.mediaFolders[asset.folderId]) {
      items.push({
        label: parent ? `Move up to “${parent.name}”` : 'Move to top level',
        onSelect: () => edit('Move media', (p) => moveMediaToFolder(p, [asset.id], parent?.id)),
      });
    }
    items.push('separator', {
      label: 'Remove from project',
      description: 'Removes it from the library (and any clips using it); the file stays on disk',
      danger: true,
      onSelect: () => confirmRemove(asset),
    });
    openContextMenu(x, y, items);
  };

  return (
    <div
      className={`media-card${offline ? ' offline' : ''}${previewing ? ' previewing' : ''}`}
      draggable
      onClick={() => openSourcePreview(asset.id)}
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_MEDIA, asset.id);
        e.dataTransfer.effectAllowed = 'copyMove';
        activeDrag.mediaId = asset.id;
      }}
      onDragEnd={clearActiveDrag}
      onDoubleClick={() => insertMedia(asset.id)}
      onContextMenu={(e) => {
        e.preventDefault();
        contextMenu(e.clientX, e.clientY);
      }}
      {...tip(
        `${asset.name}${taken}\nClick to preview, double-click to add at the playhead, drag onto a track or a folder`,
      )}
    >
      <div className="media-thumb">
        {media?.thumbnailUrl ? (
          <img src={media.thumbnailUrl} alt="" draggable={false} />
        ) : (
          <Icon size={22} className="muted" />
        )}
        <span className="media-kind">
          <Icon size={11} />
        </span>
        {uses > 0 && (
          <span className="media-used" {...tip(`Used in the project (${uses} clip${uses === 1 ? '' : 's'})`)}>
            <CircleCheck size={12} />
          </span>
        )}
        {asset.duration !== undefined && (
          <span className="media-duration">{formatDuration(asset.duration)}</span>
        )}
        {pending && (
          <span
            className="media-status"
            {...tip('Preparing previews (thumbnail, waveform, filmstrip, proxy)…')}
          >
            <Loader2 size={12} className="spin" />
            {progress !== undefined && ` ${Math.round(progress * 100)}%`}
          </span>
        )}
        {failed && (
          <button
            className="media-status failed"
            {...tip('Preparing some previews failed — click to retry')}
            onClick={() => void mediaService.retry(asset.id)}
          >
            <RotateCw size={12} /> retry
          </button>
        )}
        {offline && (
          <button
            className="media-status failed"
            {...tip(`${media?.message ?? 'File not available'} — click to look again`)}
            onClick={() => void mediaService.retry(asset.id)}
          >
            <AlertTriangle size={12} /> offline
          </button>
        )}
      </div>
      <div className="media-name">{asset.name}</div>
      {detail && <div className="media-detail muted">{detail}</div>}
      <div className="media-actions">
        <button
          className="btn ghost small icon"
          {...tip(`Remove “${asset.name}” from the project`)}
          onClick={(e) => {
            e.stopPropagation();
            confirmRemove(asset);
          }}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}
